import type { Namespace } from '../types';

// DE drafted — queued for native review in the task file's "DE review queue".
export const common = {
  // R-239: each OTP box needs its own name for a screen reader — "digit 3 of 6"
  // is the only way the segmented field is navigable without sight.
  otpDigit: { de: 'Ziffer {n} von {total}', en: 'digit {n} of {total}' },
  language: { de: 'Sprache', en: 'Language' },
  preferences: { de: 'Präferenzen', en: 'Preferences' },
  email: { de: 'E-Mail', en: 'Email' },
  password: { de: 'Passwort', en: 'Password' },
  cancel: { de: 'Abbrechen', en: 'Cancel' },
  retry: { de: 'Erneut versuchen', en: 'Retry' },
  loadMore: { de: 'Weitere laden', en: 'Load more' },
  // A multi-location read where SOME locations failed: their rows are missing, not empty.
  someLocationsFailed: {
    de: '{failed} von {total} Standorten konnten nicht gelesen werden — deren Zeilen fehlen.',
    en: "Couldn't read {failed} of {total} locations — their rows are missing.",
  },
  loadedCount: { de: '{shown} von {total} geladen', en: '{shown} of {total} loaded' },
  // A server-paged list that reports no total: say what is on screen, never guess an "of N".
  loadedOnly: { de: '{shown} geladen', en: '{shown} loaded' },
  refresh: { de: 'Aktualisieren', en: 'Refresh' },
  refreshList: { de: 'Liste neu laden', en: 'Refresh list' },
  refreshFailed: {
    de: 'Aktualisierung fehlgeschlagen — zuletzt geladener Stand.',
    en: "Couldn't refresh — showing the last loaded list.",
  },
  tryAgain: { de: 'Bitte erneut versuchen.', en: 'Please try again.' },
  passwordsMismatch: { de: 'Passwörter stimmen nicht überein', en: 'Passwords do not match' },
  firstName: { de: 'Vorname', en: 'First name' },
  lastName: { de: 'Nachname', en: 'Last name' },
  phone: { de: 'Telefon', en: 'Phone' },
  role: { de: 'Rolle', en: 'Role' },
  roles: { de: 'Rollen', en: 'Roles' },
  none: { de: 'Keine', en: 'None' },
  copy: { de: 'Kopieren', en: 'Copy' },
  userType: { de: 'Benutzertyp', en: 'User type' },
  administrator: { de: 'Administrator', en: 'Administrator' },
  member: { de: 'Mitglied', en: 'Member' },
  rolesLoadError: { de: 'Rollen konnten nicht geladen werden.', en: "Couldn't load roles." },
  adminFullAccess: {
    de: 'Administratoren haben vollen Zugriff — Rollen sind nicht anwendbar.',
    en: 'Administrators have full access — roles do not apply.',
  },
  copyFailed: { de: 'Link konnte nicht kopiert werden', en: 'Could not copy link' },
  loadFailed: { de: 'Laden fehlgeschlagen — bitte neu laden.', en: 'Load failed — please reload.' },
  open: { de: 'Öffnen', en: 'Open' },
  close: { de: 'Schließen', en: 'Close' },
  create: { de: 'Anlegen', en: 'Create' },
  add: { de: 'Hinzufügen', en: 'Add' },
  clear: { de: 'Zurücksetzen', en: 'Clear' },
  all: { de: 'alle', en: 'all' },
  saving: { de: 'Speichern…', en: 'Saving…' },
  details: { de: 'Details', en: 'Details' },
  from: { de: 'von', en: 'From' },
  to: { de: 'bis', en: 'To' },
  min: { de: 'Min', en: 'Min' },
  max: { de: 'Max', en: 'Max' },
  unit: { de: 'Einheit', en: 'Unit' },
  site: { de: 'Standort', en: 'Site' },
  sites: { de: 'Standorte', en: 'Sites' },
  target: { de: 'Ziel', en: 'Target' },
  saveChanges: { de: 'Änderungen speichern', en: 'Save changes' },
  // CREATE says Save, EDIT says Update. One form often serves both, so the verb
  // is picked from whether the record has an id, never hard-coded.
  updateChanges: { de: 'Änderungen aktualisieren', en: 'Update changes' },
  saveTitle: { de: 'Änderungen speichern?', en: 'Save changes?' },
  type: { de: 'Typ', en: 'Type' },
  status: { de: 'Status', en: 'Status' },
  priority: { de: 'Priorität', en: 'Priority' },
  displayName: { de: 'Anzeigename', en: 'Display name' },
  location: { de: 'Ort', en: 'Location' },
  edit: { de: 'Bearbeiten', en: 'Edit' },
  delete: { de: 'Löschen', en: 'Delete' },
  remove: { de: 'Entfernen', en: 'Remove' },
  updated: { de: 'Aktualisiert', en: 'Updated' },
  iotUnreachable: {
    de: 'Der IoT-Query-Dienst ist nicht erreichbar. Bitte gleich erneut versuchen.',
    en: 'The IoT query service is unreachable. Retry shortly.',
  },

  // R-355 — what the `~` before a reading means. Lives in `common` because the
  // SAME sentence is shown by the gauge, the summary chip, the Sensors table and
  // the history chart; four copies in four namespaces would be four chances to
  // drift apart.
  estimatedHelp: {
    de: 'Geschätzt aus dem Rohwert mit dem Standardfaktor — Raum mit einem PAR-Messgerät kalibrieren',
    en: 'Estimated from the raw count with the default factor — calibrate this room with a PAR meter',
  },

  // Relative-time labels (useRelTime)
  never: { de: 'Nie', en: 'Never' },
  justNow: { de: 'Gerade eben', en: 'Just now' },
  secondsAgo: { de: 'vor {n} s', en: '{n}s ago' },
  minutesAgo: { de: 'vor {n} min', en: '{n} min ago' },
  hoursAgo: { de: 'vor {n} h', en: '{n} h ago' },
  daysAgo: { de: 'vor {n} d', en: '{n} d ago' },
} as const satisfies Namespace;
